import { useProtecaoFormulario } from "@/shared/hooks/useProtecaoSaida";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useIgreja } from "@/core/hooks/useIgreja";
import { useVoltarOu } from "@/modules/visitantes/hooks/useVoltarOu";
import { FluxoCadastro } from "@/modules/visitantes/components/FluxoCadastro";
import { SemPermissao } from "@/modules/visitantes/components/PaginaFormulario";
import {
  useCriarFamilia,
  useCriarVisitante,
  usePapelVisitantes,
  useVisitantes,
} from "@/modules/visitantes/hooks/useVisitantes";
import type {
  DadosFamiliar,
  DadosVisitante,
} from "@/modules/visitantes/services/visitantesService";

/**
 * Cadastro de visitante — só o ministério responsável (o banco revalida).
 * Dois passos quando a pessoa veio com a família (ver `FluxoCadastro`).
 */
export function NovoVisitantePage() {
  const navigate = useNavigate();
  const voltar = useVoltarOu("/visitantes");
  const protecao = useProtecaoFormulario();
  const { papel, carregando } = usePapelVisitantes();
  const { data: existentes = [] } = useVisitantes(papel === "gestao");
  const { data: igreja } = useIgreja();
  const criar = useCriarVisitante();
  const criarFamilia = useCriarFamilia();
  const [erro, setErro] = useState<string | null>(null);

  if (carregando) return <p className="px-4 py-10 text-center text-sm text-muted">Carregando…</p>;
  if (papel !== "gestao")
    return <SemPermissao texto="Só o ministério responsável cadastra visitantes." />;

  const contato = igreja
    ? { email: igreja.contato_email, telefone: igreja.contato_telefone }
    : null;

  async function enviar(dados: DadosVisitante, consentimento: boolean) {
    setErro(null);
    try {
      const id = await criar.mutateAsync({ dados, consentimento });
      protecao.liberar();
      navigate("/visitantes", {
        replace: true,
        state: { aviso: `${dados.nome} foi cadastrado(a).`, novoId: id },
      });
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Não foi possível cadastrar o visitante.");
    }
  }

  async function enviarFamilia(
    principal: DadosVisitante,
    membros: DadosFamiliar[],
    consentimento: boolean,
  ) {
    setErro(null);
    try {
      const r = await criarFamilia.mutateAsync({ principal, membros, consentimento });
      protecao.liberar();
      navigate("/visitantes", {
        replace: true,
        state: {
          aviso: `${principal.nome} e mais ${membros.length} da família foram cadastrados.`,
          novoId: r.ids[0],
        },
      });
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Não foi possível cadastrar a família.");
    }
  }

  return (
    <FluxoCadastro
      modo="cadastro"
      protecao={protecao}
      titulo="Novo visitante"
      descricao="Só o essencial: quem é, quando visitou e como falar com a pessoa."
      existentes={existentes}
      contatoIgreja={contato}
      enviando={criar.isPending || criarFamilia.isPending}
      erro={erro}
      onLimparErro={() => setErro(null)}
      onEnviarSimples={enviar}
      onEnviarFamilia={enviarFamilia}
      onCancelar={voltar}
    />
  );
}
